<script lang="ts" setup>
import { ref } from 'vue'
import { useNotificationsStore } from '@/store/notifications'
import { usePayrollChange } from '@/app/core/composable/routes/useChangePayroll'
import { getErrorCode } from '@/app/core/composable/general'
import { DBtn, DTextField, DTable, DLoading } from 'front-library'
import { useI18n } from 'vue-i18n'
import {
  getContractTypes,
  ContractTypes
} from '@/app/core/services/contract-types'
import { useUserStore } from '@/store/user'
import { useRouter } from 'vue-router'
usePayrollChange(() => window.location.reload())

/* store, router, notifications */
const router = useRouter()
const store = useUserStore()
const notifications = useNotificationsStore()
const { t } = useI18n()

/* Datos componente Loading */
const loading = ref(false)

/* Datos */
const search = ref()
const tableContractTypes = ref(null)

/* Datos componente table */
const columns = ref([
  { name: 'code', title: t('contractTypes.list.code'), table: 'code' },
  { name: 'name', title: t('contractTypes.list.name'), table: 'name' },
  {
    name: 'updated_at',
    title: t('contractTypes.list.lastUpdate'),
    table: 'updated_at'
  }
])
const rows = ref<ContractTypes[]>([])
const perPage = ref(0)
const offset = ref(0)
const totalRows = ref(0)
const sortBy = ref('')

/* Redirect's */
const createContractType = () => {
  void router.push({ name: 'CreateContractType' })
}
const updateContractType = (id: any) => {
  void router.push({ name: 'UpdateContractType', params: { id } })
}
const getContractType = (id: any) => {
  void router.push({ name: 'GetContractType', params: { id } })
}

/* Listar motivos de ingreso */
const contractTypes = async () => {
  loading.value = true
  try {
    const data: any = await getContractTypes(
      offset.value,
      perPage.value,
      sortBy.value,
      search.value === '' ? undefined : search.value
    )
    rows.value = data.results
    totalRows.value = data.totalItems
    loading.value = false
  } catch (error: any) {
    loading.value = false
    const code = getErrorCode(error.response.data?.code)
    notifications.addNotification({
      title: t('error.titles.' + code),
      text: t('error.messages.' + code),
      type: 'error',
      time: 10000
    })
  }
}
</script>
<template>
  <DLoading v-show="loading" :message="t('loading.data')" />
  <div class="h-full px-5 overflow-auto scroll">
    <div class="bg-gray-50 pb-4">
      <div class="leading-4 pt-responsive">
        <p
          class="text-3xl font-semibold text-shadow"
          v-html="t('contractTypes.list.title')" />
        <p
          class="text-gray-500 text-shadow"
          v-html="t('contractTypes.list.description')" />
      </div>
      <div class="sm:flex justify-between pt-8">
        <div class="flex mb-3 sm:mb-0 max-w-2xl sm:w-2/3 md:w-1/2 lg:w-5/12">
          <DTextField
            :placeholder="t('form.button.search') + '...'"
            :icon="'SearchLgIcon'"
            class="mr-2 w-full"
            v-model="search"
            @keyup.enter="contractTypes" />
          <DBtn
            @click.prevent="contractTypes"
            v-html="t('form.button.search')" />
        </div>
        <DBtn
          v-if="store.hasPermission('contract_type:create')"
          :icon="'PlusIcon'"
          type="button"
          @click="createContractType()">
          {{
            t('form.button.create', { form: t('contractTypes.list.title', 0) })
          }}
        </DBtn>
      </div>
    </div>
    <DTable
      ref="tableContractTypes"
      :columns="columns"
      :rows="rows"
      :total-rows="totalRows"
      :select-options="false"
      :text-no-data="t('table.noData')"
      :text-per-page="t('table.rowsPerPage')"
      @perPage=";(perPage = $event), contractTypes()"
      @offset="offset = $event"
      @sort=";(sortBy = $event), contractTypes()">
      <template #actions="data: any">
        <div class="flex py-2.5 justify-end gap-1">
          <Popper
            v-if="store.hasPermission('contract_type:get')"
            arrow
            offsetDistance="12"
            :content="t('table.action.detail')"
            :hover="true"
            placement="top"
            class="tooltip">
            <div
              class="bg-gray-50 rounded-md py-2"
              @click="getContractType(data.id)">
              <SearchMdIcon class="h-6 w-6 mx-2 cursor-pointer text-gray-600" />
            </div>
          </Popper>
          <Popper
            v-if="
              !data.is_inactive && store.hasPermission('contract_type:update')
            "
            arrow
            offsetDistance="12"
            :content="t('table.action.edit')"
            :hover="true"
            placement="top"
            class="tooltip">
            <div
              class="bg-gray-50 rounded-md py-2"
              @click="updateContractType(data.id)">
              <Edit03Icon class="h-6 w-6 mx-2 cursor-pointer text-gray-600" />
            </div>
          </Popper>
        </div>
      </template>
    </DTable>
  </div>
</template>
